import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Badge } from '@/components/ui/badge';
import { Play, Edit2, Trash2 } from 'lucide-react';

interface StartNodeData {
  label: string;
  trigger?: string; // e.g., "Manual", "API Call", "Schedule", "Event"
  description?: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

function StartNode({ data, selected }: NodeProps<StartNodeData>) {
  return (
    <div
      className={`bg-white rounded-lg border-2 shadow-lg min-w-[200px] max-w-[280px] ${
        selected ? 'border-green-500 shadow-green-200' : 'border-green-300'
      }`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-2 rounded-t-md flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4" />
          <span className="font-semibold text-sm">START</span>
        </div>
        <div className="flex gap-1 nodrag nopan" style={{ pointerEvents: 'auto' }}>
          {data.onEdit && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-green-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('✏️ Edit clicked!', data.label);
                if (data.onEdit) {
                  data.onEdit();
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Edit node"
            >
              <Edit2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
          {data.onDelete && (
            <button
              type="button"
              className="h-6 w-6 p-1 flex items-center justify-center hover:bg-red-600/20 rounded transition-colors cursor-pointer nodrag"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('🗑️ Delete clicked!', data.label);
                if (data.onDelete) {
                  data.onDelete();
                }
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              title="Delete node"
            >
              <Trash2 className="w-3 h-3 text-white" style={{ pointerEvents: 'none' }} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        <div className="font-semibold text-sm text-gray-900">{data.label}</div>
        
        {data.description && (
          <div className="text-xs text-gray-600">{data.description}</div>
        )}

        {data.trigger && (
          <div className="pt-2 border-t">
            <Badge variant="secondary" className="text-xs">
              {data.trigger}
            </Badge>
          </div>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 !bg-green-500"
      />
    </div>
  );
}

export default memo(StartNode);

